import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Crown, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SECTIONS, activeSection } from "./crmNav";

const SubTabs = ({ section, path }) => (
  <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 sm:px-6 py-2 border-b border-white/10 bg-[#0B241B]/80" data-testid="crm-subtabs">
    {section.subs.map(([label, to]) => {
      const on = path === to || (to !== "/crm" && path.startsWith(to + "/"));
      return (
        <Link key={to} to={to} data-testid={`crm-subtab-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-brass text-emerald-950 border-brass font-semibold" : "border-white/10 text-emerald-100/70 hover:bg-white/5"}`}>{label}</Link>
      );
    })}
  </div>
);

export function CrmShell({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const section = activeSection(location.pathname);
  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#071912] text-slate-100 flex">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-[#0B241B] border-r border-white/10 sticky top-0 h-screen">
        <Link to="/crm" className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <span className="grid place-items-center w-9 h-9 rounded-md bg-white/10"><Crown className="w-4.5 h-4.5" style={{ color: "#C59B27" }} /></span>
          <div className="leading-none">
            <div className="font-serif text-white text-base">Emerald Crown</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-brass">Sales CRM</div>
          </div>
        </Link>
        <nav className="flex flex-col gap-1 px-3 py-4 flex-1">
          {SECTIONS.map((s) => (
            <NavLink key={s.key} to={s.to} data-testid={`crmnav-${s.key}`}
              className={`flex items-center gap-3 text-sm px-3 py-3 rounded-lg transition-colors ${section.key === s.key ? "bg-brass text-emerald-950 font-semibold" : "text-emerald-100/70 hover:bg-white/5 hover:text-white"}`}>
              <s.icon className="w-4.5 h-4.5" /> {s.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <Link to="/admin" className="flex items-center gap-2 text-xs text-emerald-100/60 hover:text-white px-3 py-2"><ExternalLink className="w-3.5 h-3.5" /> Website admin</Link>
          <button onClick={doLogout} data-testid="crm-logout" className="w-full flex items-center gap-2 text-xs text-emerald-100/60 hover:text-white px-3 py-2"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-[#0B241B]/95 backdrop-blur border-b border-white/10">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6">
            <div className="flex items-center gap-2.5 min-w-0">
              <Crown className="w-4.5 h-4.5 lg:hidden shrink-0" style={{ color: "#C59B27" }} />
              <h1 className="text-lg sm:text-xl font-serif text-white truncate" data-testid="crm-page-title">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-emerald-100/80 hidden sm:block">{user?.name || user?.email}</span>
              {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
              <button onClick={doLogout} className="lg:hidden text-emerald-100/70 hover:text-white" data-testid="crm-logout-mobile"><LogOut className="w-4.5 h-4.5" /></button>
            </div>
          </div>
          {section.subs.length > 1 && <SubTabs section={section} path={location.pathname} />}
        </header>
        <main className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-5 pb-28 lg:pb-8"><ErrorBoundary key={location.pathname}>{children}</ErrorBoundary></main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-[#0B241B]/95 backdrop-blur border-t border-white/10 grid grid-cols-5"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="crm-bottom-nav">
        {SECTIONS.map((s) => (
          <Link key={s.key} to={s.to} data-testid={`crm-tab-${s.key}`}
            className={`flex flex-col items-center justify-center gap-1 h-16 text-[10px] font-medium transition-colors ${section.key === s.key ? "text-brass" : "text-emerald-100/60"}`}>
            <s.icon className="w-5 h-5" />{s.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
